import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, ArrowLeftRight, FolderOpen, TrendingUp, FileText, Settings, LogOut, RefreshCcw, Target, Menu, Landmark } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Layout = ({ setIsAuthenticated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    navigate('/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/' },
    { icon: ArrowLeftRight, label: 'Transactions', path: '/transactions' },
    { icon: RefreshCcw, label: 'Recurring', path: '/recurring' },
    { icon: Target, label: 'Savings', path: '/savings' },
    { icon: FolderOpen, label: 'Categories', path: '/categories' },
    { icon: TrendingUp, label: 'Budgets', path: '/budgets' },
    { icon: FileText, label: 'Reports', path: '/reports' },
    { icon: Landmark, label: 'Wealth Portfolio', path: '/wealth-portfolio' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userInitials = user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';

  const SidebarContent = ({ mobile = false }) => (
    <div className="flex flex-col h-full bg-card">
      {/* Logo */}
      <div className="p-6 border-b border-border">
        <h1 className="text-2xl font-bold tracking-tight" data-testid="app-title">Spend Pro</h1>
      </div>

      {/* User Profile */}
      <div className="p-6 border-b border-border" data-testid="user-profile">
        <div className="flex items-center space-x-3">
          <Avatar>
            <AvatarImage src="https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwbWFuJTIwc21pbGluZ3xlbnwwfHx8fDE3NzEzMDU2MjF8MA&ixlib=rb-4.1.0&q=85" />
            <AvatarFallback>{userInitials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate" data-testid="user-name">{user.name || 'User'}</p>
            <p className="text-xs text-muted-foreground truncate" data-testid="user-email">{user.email || ''}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="sidebar-navigation">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Button
              key={item.path}
              variant={isActive ? 'secondary' : 'ghost'}
              className="w-full justify-start"
              onClick={() => {
                navigate(item.path);
                if (mobile) setOpen(false);
              }}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <Icon className="mr-3 h-4 w-4" />
              {item.label}
            </Button>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-border">
        <Button
          variant="ghost"
          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background flex-col md:flex-row" data-testid="main-layout">
      {/* Mobile Header */}
      <header className="md:hidden border-b border-border p-4 flex items-center justify-between bg-card sticky top-0 z-30">
        <div className="flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent mobile={true} />
            </SheetContent>
          </Sheet>
          <h1 className="text-xl font-bold tracking-tight">Spend Pro</h1>
        </div>
        <Avatar className="h-8 w-8">
          <AvatarImage src="https://images.unsplash.com/photo-1576558656222-ba66febe3dec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBoZWFkc2hvdCUyMHBvcnRyYWl0JTIwbWFuJTIwc21pbGluZ3xlbnwwfHx8fDE3NzEzMDU2MjF8MA&ixlib=rb-4.1.0&q=85" />
          <AvatarFallback>{userInitials}</AvatarFallback>
        </Avatar>
      </header>

      {/* Desktop Sidebar - Hidden on Mobile */}
      <aside className="hidden md:flex w-64 border-r border-border flex-col bg-card fixed inset-y-0 z-40" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto md:ml-64" data-testid="main-content">
        <div className="p-4 md:p-8 space-y-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
